import { useState, useEffect } from 'react';
import { Globe, Save, Trash2, Loader2, Plus } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

const METHOD_LABELS = {
  mpesa: 'M-PESA', orange: 'Orange Money', mtn: 'MTN Money',
  bank: 'Virement bancaire', card: 'Carte (Stripe)', etransfer: 'e-Transfert',
};
const ALL_METHODS = ['mpesa', 'orange', 'mtn', 'bank', 'card', 'etransfer'];

export default function AdminCountryPricing({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [drafts, setDrafts] = useState({}); // country_code -> {currency, prices, methods}
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiCall('/admin/country-pricing', 'GET', null, token);
      setData(r);
      const d = {};
      (r.countries || []).forEach(c => { d[c.country_code] = { currency: c.currency, prices: { ...c.prices }, methods: c.methods || [] }; });
      setDrafts(d);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="text-center py-10 text-white/60"><Loader2 className="animate-spin mx-auto" /></div>;

  const modules = data?.modules || [];
  const allCountries = data?.all_countries || [];
  const configured = Object.keys(drafts);

  const addCountry = () => {
    if (!newCode) return;
    const c = allCountries.find(x => x.code === newCode);
    if (!c) return;
    setDrafts(prev => ({ ...prev, [newCode]: { currency: c.currency, prices: {}, methods: [...ALL_METHODS] } }));
    setNewCode('');
  };

  const patch = (cc, obj) => setDrafts(prev => ({ ...prev, [cc]: { ...prev[cc], ...obj } }));
  const setPrice = (cc, mid, val) => setDrafts(prev => ({ ...prev, [cc]: { ...prev[cc], prices: { ...prev[cc].prices, [mid]: val } } }));
  const toggleMethod = (cc, m) => setDrafts(prev => {
    const has = prev[cc].methods.includes(m);
    return { ...prev, [cc]: { ...prev[cc], methods: has ? prev[cc].methods.filter(x => x !== m) : [...prev[cc].methods, m] } };
  });

  const save = async (cc) => {
    const d = drafts[cc];
    const prices = {};
    Object.entries(d.prices).forEach(([k, v]) => { if (v !== '' && v != null) prices[k] = Number(v); });
    setSaving(s => ({ ...s, [cc]: true }));
    try {
      await apiCall('/admin/country-pricing', 'PUT', {
        country_code: cc, currency: d.currency, prices, methods: d.methods, active: true,
      }, token);
      toast.success('Tarifs enregistrés pour ' + cc);
    } catch (e) { toast.error(e.message); }
    setSaving(s => ({ ...s, [cc]: false }));
  };

  const remove = async (cc) => {
    try {
      await apiCall(`/admin/country-pricing/${cc}`, 'DELETE', null, token);
      setDrafts(prev => { const n = { ...prev }; delete n[cc]; return n; });
      toast.success('Pays retiré');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-country-pricing">
      <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><Globe size={18} className="text-emerald-400" />Prix par pays</h3>
      <p className="text-sm text-white/60 mb-4">Définissez la devise, le prix de chaque module et les modes de paiement disponibles pour chaque pays. Les pays non configurés utilisent le prix par défaut (onglet « Tarifs »).</p>

      <div className="flex gap-2 mb-6">
        <select value={newCode} onChange={e => setNewCode(e.target.value)} data-testid="add-country-select"
          className="flex-1 bg-slate-900 border border-white/10 rounded-lg p-2.5 text-sm">
          <option value="">+ Ajouter un pays…</option>
          {allCountries.filter(c => !configured.includes(c.code)).map(c => (
            <option key={c.code} value={c.code}>{c.name} — {c.currency}</option>
          ))}
        </select>
        <button onClick={addCountry} disabled={!newCode} data-testid="add-country-btn"
          className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold px-4 rounded-lg flex items-center gap-1"><Plus size={16} />Ajouter</button>
      </div>

      {configured.length === 0 && <div className="text-center text-white/40 py-6 text-sm">Aucun pays configuré. Le prix par défaut s'applique partout.</div>}

      <div className="space-y-4">
        {configured.map(cc => {
          const c = allCountries.find(x => x.code === cc) || { name: cc, currency: drafts[cc].currency };
          const d = drafts[cc];
          return (
            <div key={cc} className="rounded-2xl p-4 border bg-white/5 border-white/10" data-testid={`country-card-${cc}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="font-bold">{c.name} <span className="text-xs text-white/40">({cc})</span></div>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-white/50">Devise</label>
                  <input value={d.currency} onChange={e => patch(cc, { currency: e.target.value.toUpperCase() })}
                    className="w-24 bg-slate-900 border border-white/10 rounded-lg p-1.5 text-sm" data-testid={`currency-${cc}`} />
                  <button onClick={() => remove(cc)} className="text-red-300 hover:text-red-200" data-testid={`remove-country-${cc}`}><Trash2 size={16} /></button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                {modules.map(m => (
                  <div key={m.id} className="flex items-center gap-2">
                    <span className="text-sm flex-1 truncate">{m.icon} {m.title_fr || m.id}</span>
                    <input type="number" step="0.01" value={d.prices[m.id] ?? ''} onChange={e => setPrice(cc, m.id, e.target.value)}
                      placeholder="Prix" data-testid={`cprice-${cc}-${m.id}`}
                      className="w-28 bg-slate-900 border border-white/10 rounded-lg p-1.5 text-sm" />
                    <span className="text-xs text-white/40 w-10">{d.currency}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {ALL_METHODS.map(m => (
                  <button key={m} onClick={() => toggleMethod(cc, m)} data-testid={`method-${cc}-${m}`}
                    className={`text-xs px-2.5 py-1 rounded-full border ${d.methods.includes(m) ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200' : 'bg-white/5 border-white/10 text-white/40'}`}>
                    {METHOD_LABELS[m]}
                  </button>
                ))}
              </div>

              <button onClick={() => save(cc)} disabled={saving[cc]} data-testid={`save-country-${cc}`}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold py-1.5 px-4 rounded-lg text-sm flex items-center gap-1.5">
                {saving[cc] ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}Sauvegarder
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
